import { Component, Input, forwardRef, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, finalize } from 'rxjs/operators';
import { of } from 'rxjs';
import { environment } from '../../services/global-environment';
import { I18nService } from '../../services/i18n.service';
import { TranslatePipe } from '../../pipes/translate.pipe';

export interface Department {
  id: string;
  name: string;
  level: number;
  parentId?: string;
  children?: Department[];
}

// API 回應介面
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

// API 回應中的部門節點介面
interface ApiDepartmentNode {
  id: string;
  name: string;
  type: string;
  children?: ApiDepartmentNode[];
}

@Component({
  selector: 'app-form-department-selector',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  template: `
    <div class="form-section">
      <label [attr.for]="id">{{ label }}</label>



      <!-- 無部門資料提醒 -->
      <div *ngIf="!isLoading && departments.length === 0 " class="no-data-warning" style="margin-bottom: 10px; padding: 8px; background-color: #fff3cd; border: 1px solid #ffeaa7; border-radius: 4px; color: #856404; font-size: 14px;">
        {{ 'form.department_selector.no_data_message' | translate }}
      </div>

      <!-- 載入狀態顯示 -->
      <div *ngIf="isLoading" class="loading-message" style="margin-bottom: 10px; padding: 8px; background-color: #e6f3ff; border: 1px solid #99ccff; border-radius: 4px; color: #0066cc; font-size: 14px;">
        {{ 'form.department_selector.loading' | translate }}
      </div>

      <!-- 搜尋框 -->
      <input
        type="text"
        [id]="id + '_search'"
        [(ngModel)]="searchTerm"
        (ngModelChange)="onSearchChange()"
        [placeholder]="'form.department_selector.search_placeholder' | translate"
        [disabled]="isLoading"
        class="form-input"
        style="margin-bottom: 10px;"
      />

      <!-- 部門選擇下拉選單 -->
      <select
        [id]="id"
        [name]="name"
        [ngModel]="value"
        (ngModelChange)="onSelectionChange($event)"
        (blur)="onTouched()"
        [disabled]="isLoading"
        class="form-input"
      >
        <option value="" disabled>{{ 'form.department_selector.placeholder' | translate }}</option>
        <option
          *ngFor="let dept of filteredDepartments"
          [value]="dept.id"
          [style.padding-left.px]="dept.level * 20"
        >
          {{ getDepartmentDisplayName(dept) }}
        </option>
      </select>

      <!-- 顯示選中的部門資訊 -->
      <div *ngIf="selectedDepartment" class="selected-info" style="margin-top: 8px; padding: 8px; background-color: #e8f4fd; border-radius: 4px; font-size: 14px;">
        {{ 'form.department_selector.selected' | translate }}：{{ selectedDepartment.name }}
        <span *ngIf="selectedDepartment.level > 0"> ({{ getLevelText(selectedDepartment.level) }})</span>
      </div>
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormDepartmentSelectorComponent),
      multi: true
    }
  ],
  styleUrls: ['total-component.scss']
})
export class FormDepartmentSelectorComponent implements ControlValueAccessor, OnInit {
  @Input() label: string = '部門選擇'; // 標籤
  @Input() id: string = ''; // id 用於對應 label
  @Input() name: string = ''; // name 用於表單提交
  @Input() placeholder: string = '請選擇部門'; // 佔位符
  @Input() searchPlaceholder: string = '搜尋部門...'; // 搜尋佔位符

  value: string = ''; // 綁定的值
  searchTerm: string = '';
  departments: Department[] = [];
  filteredDepartments: Department[] = [];
  selectedDepartment: Department | null = null;
  isLoading: boolean = false; // 載入狀態
  errorMessage: string = ''; // 錯誤訊息

  onChange: (value: any) => void = () => {};
  onTouched: () => void = () => {};

  constructor(private http: HttpClient, private i18nService: I18nService) {}

  ngOnInit(): void {
    this.initializeDepartments();
  }

  // 初始化部門資料 - 使用真實 API 調用
  private async initializeDepartments(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = '';

    try {
      // 從環境變數取得 companyid
      const companyid = environment.companyid;
      const assistantNo = 'ALL';

      if (!companyid) {
        throw new Error(this.i18nService.translate('form.department_selector.no_company_id'));
      }

      // 調用 API
      const url = `${environment.apiUrl}/api/departments/${companyid}/${assistantNo}`;

      this.http.get<ApiResponse<ApiDepartmentNode>>(url)
        .pipe(
          catchError((error: HttpErrorResponse) => {
            console.warn('載入部門資料失敗:', error);
            this.errorMessage = error.error?.message || this.i18nService.translate('form.department_selector.load_error_fallback');

            // // API 失敗時回退到 Mock 資料
            // this.loadMockDepartments();
            return of(null as ApiResponse<ApiDepartmentNode> | null);
          }),
          finalize(() => {
            this.isLoading = false;
            this.filteredDepartments = [...this.departments];
          })
        )
        .subscribe(response => {
          if (response?.success && response.data) {
            // 轉換 API 資料為元件所需格式
            this.departments = this.convertApiDataToDepartments(response.data);
          } else if (!this.departments.length && !this.errorMessage) {
            // // 如果 API 成功但無資料，且沒有錯誤訊息，使用 Mock 資料
            // this.loadMockDepartments();
          }
        });

    } catch (error) {
      console.error('初始化部門資料錯誤:', error);
      this.errorMessage = this.i18nService.translate('form.department_selector.init_error_fallback');
      this.isLoading = false;

      // // 錯誤時回退到 Mock 資料
      // this.loadMockDepartments();
      this.filteredDepartments = [...this.departments];
    }
  }

  // 轉換 API 資料為扁平化部門陣列
  private convertApiDataToDepartments(apiData: ApiDepartmentNode): Department[] {
    const departments: Department[] = [];

    const traverseNode = (node: ApiDepartmentNode, level: number, parentId?: string) => {
      // 只有當節點類型是部門或公司時才加入
      if (node.type === 'department' || node.type === 'company') {
        departments.push({
          id: node.id,
          name: node.name,
          level: level,
          parentId: parentId
        });
      }

      // 遞迴處理子節點
      if (node.children && node.children.length > 0) {
        node.children.forEach(child => {
          traverseNode(child, level + 1, node.id);
        });
      }
    };

    // 從根節點開始遍歷
    traverseNode(apiData, 0);

    return departments;
  }

  // // 載入 Mock 資料作為回退方案
  // private loadMockDepartments(): void {
  //   // 原本的 Mock 部門資料 - 保留作為 API 失敗時的回退方案
  //   this.departments = [
  //   ];
  // }

  onSearchChange(): void {
    if (!this.searchTerm.trim()) {
      this.filteredDepartments = [...this.departments];
    } else {
      this.filteredDepartments = this.departments.filter(dept =>
        dept.name.toLowerCase().includes(this.searchTerm.toLowerCase())
      );
    }
  }

  onSelectionChange(departmentId: string): void {
    this.value = departmentId;
    this.selectedDepartment = this.departments.find(dept => dept.id === departmentId) || null;
    
    // 傳遞完整的部門資訊
    const departmentValue = this.selectedDepartment ? {
      type: 'department-selector',
      value: this.selectedDepartment.name,
      id: this.selectedDepartment.id,
      name: this.selectedDepartment.name
    } : {
      type: 'department-selector',
      value: '',
      id: '',
      name: ''
    };
    
    this.onChange(departmentValue);
  }

  getDepartmentDisplayName(dept: Department): string {
    const prefix = '　'.repeat(dept.level); // 全形空白字元用於縮排
    return prefix + dept.name;
  }

  getLevelText(level: number): string {
    const levelKeys = [
      'form.department_selector.level_department',
      'form.department_selector.level_group',
      'form.department_selector.level_subgroup',
      'form.department_selector.level_unit'
    ];
    const key = levelKeys[level] || 'form.department_selector.level_unit';
    return this.i18nService.translate(key);
  }

  // ControlValueAccessor 實作
  writeValue(value: any): void {
    if (value !== undefined) {
      // 處理新的增強格式
      if (typeof value === 'object' && value !== null && value.hasOwnProperty('type') && value.type === 'department-selector') {
        this.value = value.id || '';
        this.selectedDepartment = this.departments.find(dept => dept.id === this.value) || null;
      } else {
        // 處理舊格式（純字串 ID）
        this.value = value;
        this.selectedDepartment = this.departments.find(dept => dept.id === value) || null;
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    // 可以在這裡處理 disabled 狀態
  }
}
